import React, { useEffect, useRef, useState } from "react";
import { useApp } from "../context/AppContext";
import { toast } from "./ui/sonner";
import { Bot, ChevronDown, ChevronUp, Loader2, Pause, Play, RefreshCw, Settings2, Sparkles } from "lucide-react";
import { BriefPrefsDialog } from "./BriefPrefsDialog";
import { fmtD, fmtDT } from "../lib/date";

const DEPTS = [["eng_manager", "工程经理", "Eng manager"], ["online_pm", "线上项目经理", "Online PM"], ["pm", "项目经理", "PM"]];
const MGR = ["admin", "ceo", "eng_manager"];

const Bold = ({ s }) => s.split(/(\*\*[^*]+\*\*)/g).map((p, i) => (p.startsWith("**") ? <strong key={i} className="font-semibold text-slate-900">{p.slice(2, -2)}</strong> : <React.Fragment key={i}>{p}</React.Fragment>));

// 极简 Markdown：### 小节 / - 列表 / **加粗**
const Md = ({ text }) => (
  <div className="space-y-1" data-testid="dept-brief-body">
    {String(text || "").split("\n").map((ln, i) => {
      const s = ln.trim();
      if (!s) return null;
      if (s.startsWith("#")) {
        const t = s.replace(/^#+\s*/, "");
        const cls = /卡点|风险/.test(t) ? "text-red-700" : /建议/.test(t) ? "text-indigo-700" : "text-slate-800";
        return <p key={i} className={`text-xs font-bold mt-2 first:mt-0 ${cls}`} data-testid="dept-brief-section">{t}</p>;
      }
      if (/^[-*•]\s/.test(s) || /^\d+[.、]\s/.test(s)) return <p key={i} className="text-xs text-slate-700 leading-relaxed pl-3 relative before:content-['•'] before:absolute before:left-0 before:text-slate-400"><Bold s={s.replace(/^([-*•]|\d+[.、])\s/, "")} /></p>;
      return <p key={i} className="text-xs text-slate-700 leading-relaxed"><Bold s={s} /></p>;
    })}
  </div>
);

// AI 部门晨报卡：今日必办 / 卡点 / 风险 / 建议动作（按角色自动选部门；管理层可切换部门、重新生成）
//
// showKickoff：是否显示「待开工 N」统计徽章（默认显示）。
//   2026-10-14 走查清单「二、重复冗余 #12」：产品要求「待开工」只在「工程项目」页出现，
//   故「全链路运行状态」与「线上项目经理工作台」两页传 showKickoff={false}；
//   「工程项目」页保持默认显示（那里有待开工清单/操作区，徽章与之呼应）。
export const DeptBriefCard = ({ compact = false, showKickoff = true }) => {
  const { api, user, lang } = useApp();
  const zh = lang === "zh";
  const [dept, setDept] = useState(null);
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [open, setOpen] = useState(() => localStorage.getItem("dept_brief_open") !== "0");
  const [audio, setAudio] = useState({ url: null, busy: false, playing: false });
  const [prefsOpen, setPrefsOpen] = useState(false);
  const audioRef = useRef(null);
  const isMgr = MGR.includes(user?.role);
  const play = async () => {
    if (audio.playing) { audioRef.current?.pause(); setAudio((a) => ({ ...a, playing: false })); return; }
    if (audio.url) { audioRef.current?.play(); setAudio((a) => ({ ...a, playing: true })); return; }
    setAudio((a) => ({ ...a, busy: true }));
    try {
      const { data } = await api.get("/ai/dept-brief/audio", { params: dept ? { dept } : {}, responseType: "blob" });
      const url = URL.createObjectURL(data);
      setAudio({ url, busy: false, playing: true });
      setTimeout(() => audioRef.current?.play(), 50);
    } catch (e) { toast.error(zh ? "语音生成失败，请稍后重试" : "Audio failed"); setAudio((a) => ({ ...a, busy: false })); }
  };
  useEffect(() => { if (audio.url) { URL.revokeObjectURL(audio.url); setAudio({ url: null, busy: false, playing: false }); } }, [d?.id]); // eslint-disable-line
  const load = async (force = false, dp = dept) => {
    setBusy(true);
    try {
      const { data } = await api.get("/ai/dept-brief", { params: { ...(dp ? { dept: dp } : {}), ...(force ? { force: true } : {}) } });
      setD(data); if (!dept) setDept(data.dept);
      if (force) toast.success(zh ? "日报已重新生成" : "Brief regenerated");
    } catch (e) {
      if (e?.response?.status === 403) setHidden(true); else toast.error(e?.detailText || e.message);
    }
    setBusy(false);
  };
  useEffect(() => { if (user) load(); }, [user]); // eslint-disable-line
  useEffect(() => { localStorage.setItem("dept_brief_open", open ? "1" : "0"); }, [open]);
  if (hidden) return null;
  const st = d?.stats || {};
  return (
    <div className="rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50/80 via-white to-white" data-testid="dept-brief-card" data-dept={d?.dept || ""} data-ai={d?.ai ? "1" : "0"}>
      <div className="flex items-center gap-2 px-4 py-2.5 flex-wrap">
        <button data-testid="dept-brief-toggle" onClick={() => setOpen(!open)} className="flex items-center gap-1.5 text-sm font-bold text-slate-900"><Sparkles className="h-4 w-4 text-indigo-600" />{zh ? "AI 部门晨报" : "AI morning brief"}{d && <span className="text-slate-400 font-normal text-xs">· {zh ? d.deptZh : (DEPTS.find((x) => x[0] === d.dept) || [])[2]} · {fmtD(d.date)}</span>}{open ? <ChevronUp className="h-3.5 w-3.5 text-slate-400" /> : <ChevronDown className="h-3.5 w-3.5 text-slate-400" />}</button>
        {d && (
          <div className="flex items-center gap-1.5 text-[10px]" data-testid="dept-brief-stats">
            <span className="px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">{zh ? "项目" : "Projects"} {st.projects ?? 0}</span>
            <span className={`px-1.5 py-0.5 rounded-full ${st.stuck ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-500"}`}>{zh ? "卡点" : "Stuck"} {st.stuck ?? 0}</span>
            {showKickoff && st.kickoffPending > 0 && <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">{zh ? "待开工" : "Kickoff"} {st.kickoffPending}</span>}
            {st.safety > 0 && <span className="px-1.5 py-0.5 rounded-full bg-red-600 text-white">{zh ? "安全" : "Safety"} {st.safety}</span>}
            {!d.ai && <span className="px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-600" data-testid="dept-brief-fallback">{zh ? "规则汇总（AI 暂不可用）" : "Rule-based"}</span>}
          </div>
        )}
        <div className="ml-auto flex items-center gap-1.5">
          {d && (
            <button data-testid="dept-brief-play" disabled={audio.busy} onClick={play} title={zh ? "口播版晨报（约 1 分钟，开车时可听）" : "Listen (~1 min)"}
              className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors disabled:opacity-60 ${audio.playing ? "bg-indigo-600 text-white border-indigo-600" : "border-indigo-200 text-indigo-700 bg-white hover:bg-indigo-50"}`}>
              {audio.busy ? <Loader2 className="h-3 w-3 animate-spin" /> : audio.playing ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}{audio.busy ? (zh ? "生成语音…" : "Generating…") : audio.playing ? (zh ? "暂停" : "Pause") : (zh ? "播放晨报" : "Listen")}
            </button>
          )}
          {audio.url && <audio ref={audioRef} src={audio.url} onEnded={() => setAudio((a) => ({ ...a, playing: false }))} onPause={() => setAudio((a) => ({ ...a, playing: false }))} onPlay={() => setAudio((a) => ({ ...a, playing: true }))} data-testid="dept-brief-audio" />}
          {isMgr && (
            <select data-testid="dept-brief-dept" value={dept || ""} onChange={(e) => { setDept(e.target.value); load(false, e.target.value); }} className="text-[11px] border border-slate-200 rounded-md px-1.5 py-1 bg-white">
              {DEPTS.map(([k, z, en]) => <option key={k} value={k}>{zh ? z : en}</option>)}
            </select>
          )}
          <button data-testid="dept-brief-prefs" onClick={() => setPrefsOpen(true)} title={zh ? "晨报偏好：板块 / 项目 / 口播长度 / 音色" : "Preferences"} className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50"><Settings2 className="h-3 w-3" />{zh ? "偏好" : "Prefs"}</button>
          <button data-testid="dept-brief-refresh" disabled={busy} onClick={() => load(true)} title={zh ? "重新生成" : "Regenerate"} className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-60">{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}{zh ? "重新生成" : "Regenerate"}</button>
        </div>
      </div>
      <BriefPrefsDialog api={api} zh={zh} open={prefsOpen} onClose={() => setPrefsOpen(false)} onSaved={() => { setD(null); load(false); }} />
      {open && (
        <div className={`px-4 pb-4 ${compact ? "max-h-72 overflow-y-auto" : ""}`}>
          {!d && busy && <p className="text-xs text-slate-500 flex items-center gap-1.5" data-testid="dept-brief-loading"><Bot className="h-3.5 w-3.5 text-indigo-500 animate-pulse" />{zh ? "AI 正在汇总全链路数据生成今日晨报…（约 10-20 秒）" : "Generating today's brief…"}</p>}
          {d && <Md text={d.text} />}
          {d && <p className="text-[10px] text-slate-400 mt-2" data-testid="dept-brief-meta">{zh ? "生成于" : "Generated"} {fmtDT(d.generatedAt)} UTC · {zh ? "每日 07:30 自动生成并铃铛提醒；只读建议，不改变工程状态" : "Auto 07:30 daily · read-only"}</p>}
        </div>
      )}
    </div>
  );
};
